import { useEffect, useState } from 'react';
import { APP_CURRENCY, APP_LOCALE } from '../config/locale';
import {
  getIndirectCostSettings,
  saveIndirectCostSettings,
} from '../orcamentos/orcamentosApi';
import {
  allocateIndirectCost,
  type IndirectAllocationResult,
} from '../custos/custosApi';

// Critérios de rateio suportados (alinhados com a allow-list do backend).
const CRITERION_OPTIONS: { value: string; label: string }[] = [
  { value: 'faturacao', label: 'Faturação' },
  { value: 'horas', label: 'Horas' },
  { value: 'm2', label: 'Área (m²)' },
  { value: 'igual', label: 'Igual' },
];

const DEFAULT_CRITERION = 'faturacao';

const criterionLabel = (value: string): string =>
  CRITERION_OPTIONS.find((opt) => opt.value === value)?.label ?? value;

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: 'currency',
  currency: APP_CURRENCY,
});

const formatCurrency = (value: number): string => {
  if (!Number.isFinite(value)) return currencyFormatter.format(0);
  return currencyFormatter.format(value);
};

const formatPercent = (value: number): string => {
  if (!Number.isFinite(value)) return '0';
  return value.toLocaleString(APP_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const todayInput = (): string => new Date().toISOString().slice(0, 10);

// Parse a localized/plain number string into a finite number, or null when blank/invalid.
const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const normalized = trimmed.replace(/\s/g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function IndirectCostSettingsTab() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Ferramenta de distribuição. O critério é escolhido aqui e fica memorizado
  // como predefinição (lido no arranque, gravado ao confirmar).
  const [totalAmountInput, setTotalAmountInput] = useState<string>('');
  const [descriptionInput, setDescriptionInput] = useState<string>('');
  const [costDateInput, setCostDateInput] = useState<string>(todayInput());
  const [criterion, setCriterion] = useState<string>(DEFAULT_CRITERION);

  const [preview, setPreview] = useState<IndirectAllocationResult | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [distError, setDistError] = useState<string | null>(null);
  const [distSuccess, setDistSuccess] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    getIndirectCostSettings()
      .then((data) => {
        const value =
          typeof data.criterion === 'string' && data.criterion.trim() !== ''
            ? data.criterion
            : DEFAULT_CRITERION;
        setCriterion(value);
      })
      .catch(() =>
        setLoadError('Não foi possível carregar o critério de rateio.'),
      )
      .finally(() => setIsLoading(false));
  }, []);

  // Resets any prior preview/result when the distribution inputs change.
  const clearDistResult = () => {
    setPreview(null);
    setDistSuccess(null);
  };

  const validateDistribution = (): {
    totalAmount: number;
    description: string;
  } | null => {
    const totalAmount = parseNumber(totalAmountInput);
    if (totalAmount === null || totalAmount <= 0) {
      setDistError('Indique um valor total válido (> 0).');
      return null;
    }
    const description = descriptionInput.trim();
    if (description === '') {
      setDistError('A descrição é obrigatória.');
      return null;
    }
    if (!costDateInput) {
      setDistError('A data é obrigatória.');
      return null;
    }
    return { totalAmount, description };
  };

  const handlePreview = async () => {
    setDistError(null);
    setDistSuccess(null);
    const valid = validateDistribution();
    if (!valid) return;
    setIsPreviewing(true);
    try {
      const result = await allocateIndirectCost({
        criterion,
        totalAmount: valid.totalAmount,
        description: valid.description,
        costDate: costDateInput,
        confirm: false,
      });
      setPreview(result);
    } catch (err) {
      setPreview(null);
      setDistError(
        err instanceof Error && err.message
          ? err.message
          : 'Não foi possível calcular o rateio.',
      );
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    setDistError(null);
    setDistSuccess(null);
    const valid = validateDistribution();
    if (!valid) return;
    setIsConfirming(true);
    try {
      const result = await allocateIndirectCost({
        criterion,
        totalAmount: valid.totalAmount,
        description: valid.description,
        costDate: costDateInput,
        confirm: true,
      });
      // Memoriza o critério usado como predefinição (silencioso, não bloqueia o sucesso).
      saveIndirectCostSettings({ criterion }).catch(() => undefined);
      setDistSuccess(
        `${result.includedCount} obra(s) · total ${formatCurrency(result.totalAmount)} imputado.`,
      );
      setPreview(null);
    } catch (err) {
      setDistError(
        err instanceof Error && err.message
          ? err.message
          : 'Não foi possível confirmar a imputação.',
      );
    } finally {
      setIsConfirming(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="text-secondary-text py-8 text-center"
        style={{ fontSize: 'var(--text-base)' }}
      >
        A carregar parâmetros...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-destructive py-4" style={{ fontSize: 'var(--text-base)' }}>
        {loadError}
      </div>
    );
  }

  const busy = isPreviewing || isConfirming;

  return (
    <div className="space-y-4">
      <p
        className="text-secondary-text"
        style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}
      >
        Reparte um custo indireto (de estrutura) pelas obras ativas segundo um
        critério de rateio. Calcule para pré-visualizar e confirme para imputar.
      </p>

      <div
        className="bg-card border border-border p-5"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        <h4 className="mb-2">Distribuir custos indiretos</h4>
        <p
          className="text-secondary-text mb-4"
          style={{ fontSize: 'var(--text-caption)' }}
        >
          As obras ativas (adjudicadas / em execução) partilham o valor total
          segundo o critério escolhido. Sem dados para o critério, a repartição é
          igual.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Valor total (€) *
            <input
              type="number"
              min={0}
              step={0.01}
              value={totalAmountInput}
              onChange={(e) => {
                setTotalAmountInput(e.target.value);
                clearDistResult();
              }}
              placeholder="0,00"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
              style={{ borderRadius: 'var(--radius)' }}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Data *
            <input
              type="date"
              value={costDateInput}
              onChange={(e) => {
                setCostDateInput(e.target.value);
                clearDistResult();
              }}
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
              style={{ borderRadius: 'var(--radius)' }}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
            Descrição *
            <input
              type="text"
              maxLength={300}
              value={descriptionInput}
              onChange={(e) => {
                setDescriptionInput(e.target.value);
                clearDistResult();
              }}
              placeholder="Descrição do custo indireto"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
              style={{ borderRadius: 'var(--radius)' }}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Critério de rateio
            <select
              value={criterion}
              onChange={(e) => {
                setCriterion(e.target.value);
                clearDistResult();
              }}
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
              style={{ borderRadius: 'var(--radius)' }}
            >
              {CRITERION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
              Fica memorizado como predefinição.
            </span>
          </label>
        </div>

        {distError && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 mt-4"
            style={{ borderRadius: 'var(--radius)' }}
          >
            {distError}
          </div>
        )}
        {distSuccess && (
          <div
            className="border border-border bg-primary/10 text-primary px-4 py-3 mt-4"
            style={{ borderRadius: 'var(--radius)' }}
          >
            {distSuccess}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <button
            type="button"
            onClick={handlePreview}
            disabled={busy}
            className="px-4 py-2 border border-border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: 'var(--radius-button)' }}
          >
            {isPreviewing ? 'A calcular...' : 'Calcular rateio'}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || preview === null}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: 'var(--radius-button)' }}
          >
            {isConfirming ? 'A confirmar...' : 'Confirmar imputação'}
          </button>
        </div>

        {preview && (
          <div className="mt-5">
            <div className="flex flex-wrap gap-3 mb-3">
              <div
                className="bg-muted border border-border px-4 py-3"
                style={{ borderRadius: 'var(--radius-card)' }}
              >
                <div className="text-secondary-text text-xs">Total a repartir</div>
                <div
                  className="text-foreground font-medium mt-1"
                  style={{ fontSize: 'var(--text-h4)' }}
                >
                  {formatCurrency(preview.totalAmount)}
                </div>
              </div>
              <div
                className="bg-muted border border-border px-4 py-3"
                style={{ borderRadius: 'var(--radius-card)' }}
              >
                <div className="text-secondary-text text-xs">Obras incluídas</div>
                <div
                  className="text-foreground font-medium mt-1"
                  style={{ fontSize: 'var(--text-h4)' }}
                >
                  {preview.includedCount}
                </div>
              </div>
              <div
                className="bg-muted border border-border px-4 py-3"
                style={{ borderRadius: 'var(--radius-card)' }}
              >
                <div className="text-secondary-text text-xs">Critério</div>
                <div
                  className="text-foreground font-medium mt-1"
                  style={{ fontSize: 'var(--text-h4)' }}
                >
                  {criterionLabel(preview.criterion)}
                </div>
              </div>
            </div>

            {preview.equalFallback && (
              <div
                className="text-secondary-text text-sm mb-3"
                style={{ fontSize: 'var(--text-caption)' }}
              >
                (repartição igual — sem base para o critério)
              </div>
            )}

            {preview.allocations.length === 0 ? (
              <div
                className="border border-border bg-card px-4 py-6 text-secondary-text text-center"
                style={{ borderRadius: 'var(--radius)' }}
              >
                Sem obras ativas para rateio.
              </div>
            ) : (
              <div
                className="bg-card border border-border overflow-x-auto"
                style={{ borderRadius: 'var(--radius-card)' }}
              >
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted text-left">
                      <th className="px-4 py-3">Obra</th>
                      <th className="px-4 py-3 text-right">Peso (%)</th>
                      <th className="px-4 py-3 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.allocations.map((line) => (
                      <tr
                        key={line.workId}
                        className="border-b border-border last:border-b-0"
                      >
                        <td className="px-4 py-3 break-words">{line.workName}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {formatPercent(line.weightPercent)}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {formatCurrency(line.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-border bg-muted">
                      <td className="px-4 py-3 font-medium">Total</td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                        {formatCurrency(
                          preview.allocations.reduce(
                            (sum, line) => sum + line.amount,
                            0,
                          ),
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
